// Local-time date helpers — parsing "YYYY-MM-DD" with new Date() treats it as UTC,
// which causes a 1-day offset in any timezone west of UTC. These helpers parse
// components directly into local-time Date objects so the calendar shows the
// exact day the user picked.

export function parseYMDLocal(value) {
  if (!value) return undefined;
  const datePart = String(value).split("T")[0];
  const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

// "YYYY-MM-DDTHH:MM" → local-time Date
export function parseDateTimeLocal(value) {
  if (!value) return undefined;
  const [d, t = "00:00"] = String(value).split("T");
  const md = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const mt = t.match(/^(\d{2}):(\d{2})/);
  if (!md) return undefined;
  return new Date(
    Number(md[1]), Number(md[2]) - 1, Number(md[3]),
    mt ? Number(mt[1]) : 0, mt ? Number(mt[2]) : 0,
  );
}

export function formatYMD(d) {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function formatDateTimeLocal(d) {
  if (!d) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${formatYMD(d)}T${hh}:${mi}`;
}

/** Next 30-min slot at or after now — matches picker granularity. */
export function nextTimeSlotFromNow() {
  const d = new Date();
  const mins = d.getMinutes();
  const add = mins % 30 === 0 ? 0 : 30 - (mins % 30);
  d.setMinutes(mins + add, 0, 0);
  return formatDateTimeLocal(d);
}

/** Earliest pick-up respecting host delivery lead time in hours (KAN-204). */
export function minPickupWithLeadHours(leadHours = 0) {
  const lead = Math.max(0, parseInt(leadHours, 10) || 0);
  if (!lead) return nextTimeSlotFromNow();
  const base = parseDateTimeLocal(nextTimeSlotFromNow()) || new Date();
  const d = new Date(base.getTime() + lead * 3600000);
  // Align to next 30-min slot at or after the lead-adjusted instant.
  const mins = d.getMinutes();
  const add = mins % 30 === 0 ? 0 : 30 - (mins % 30);
  d.setMinutes(mins + add, 0, 0);
  return formatDateTimeLocal(d);
}

/** @deprecated Prefer minPickupWithLeadHours — days convert as days * 24. */
export function minPickupWithLeadDays(leadDays = 0) {
  const lead = Math.max(0, parseInt(leadDays, 10) || 0);
  return minPickupWithLeadHours(lead * 24);
}

export function pickupLeadTimeError(startStr, leadHours) {
  const lead = Math.max(0, parseInt(leadHours, 10) || 0);
  if (!lead || !startStr) return null;
  const min = minPickupWithLeadHours(lead);
  const s = parseDateTimeLocal(startStr);
  const minD = parseDateTimeLocal(min);
  if (s && minD && s.getTime() < minD.getTime()) {
    return `Pick-up must be at least ${lead} hour(s) from now for this vehicle`;
  }
  return null;
}

/** Resolve lead hours from vehicle doc (prefer hours; fall back days * 24). */
export function vehicleDeliveryLeadHours(vehicle) {
  if (!vehicle) return 0;
  if (vehicle.delivery_lead_hours != null && vehicle.delivery_lead_hours !== "") {
    return Math.max(0, parseInt(vehicle.delivery_lead_hours, 10) || 0);
  }
  if (vehicle.delivery_lead_days != null && vehicle.delivery_lead_days !== "") {
    return Math.max(0, parseInt(vehicle.delivery_lead_days, 10) || 0) * 24;
  }
  return 0;
}

export function extractTime(value, fallback = "10:00") {
  if (!value || !String(value).includes("T")) return fallback;
  return String(value).split("T")[1].slice(0, 5);
}

/** Add wall-clock hours to a local datetime string (parking hour presets). */
export function addHoursLocal(startStr, hours) {
  const s = parseDateTimeLocal(startStr);
  const h = Number(hours);
  if (!s || !Number.isFinite(h)) return "";
  return formatDateTimeLocal(new Date(s.getTime() + h * 3600000));
}

/** Return datetime with the same clock time as pick-up, at least minHours later. */
export function defaultReturnForPickup(startStr, minHours = 24) {
  const s = parseDateTimeLocal(startStr);
  if (!s) return "";
  const time = extractTime(startStr);
  const minEndMs = s.getTime() + minHours * 3600000;

  // Walk forward by calendar day until same clock time is >= minEndMs.
  let day = new Date(s);
  for (let i = 0; i < 366; i++) {
    const candidate = parseDateTimeLocal(`${formatYMD(day)}T${time}`);
    if (candidate && candidate.getTime() >= minEndMs) {
      return formatDateTimeLocal(candidate);
    }
    day.setDate(day.getDate() + 1);
  }
  return formatDateTimeLocal(new Date(minEndMs));
}

/** When only pick-up time changes (same calendar day), keep return datetime independent. */
export function syncReturnAfterPickupChange(startStr, endStr, prevStartStr) {
  if (!startStr) return endStr || "";
  const prevDate = prevStartStr ? String(prevStartStr).split("T")[0] : null;
  const newDate = String(startStr).split("T")[0];
  if (prevDate && prevDate === newDate) {
    if (!endStr) return defaultReturnForPickup(startStr);
    const s = parseDateTimeLocal(startStr);
    const e = parseDateTimeLocal(endStr);
    if (e && s && e.getTime() <= s.getTime()) return defaultReturnForPickup(startStr);
    return endStr;
  }
  return alignReturnWithPickup(startStr, endStr);
}

/** Sync return clock time to pick-up; keep return date when valid, else +24h same time. */
export function alignReturnWithPickup(startStr, endStr) {
  const s = parseDateTimeLocal(startStr);
  if (!s) return endStr || "";
  if (!endStr) return defaultReturnForPickup(startStr);

  const time = extractTime(startStr);
  const endDatePart = endStr.split("T")[0];
  let candidate = parseDateTimeLocal(`${endDatePart}T${time}`);
  if (!candidate || candidate.getTime() <= s.getTime()) {
    return defaultReturnForPickup(startStr);
  }
  return formatDateTimeLocal(candidate);
}

export function clientTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Toronto";
  } catch {
    return "America/Toronto";
  }
}

function partsInTimeZone(instant, timeZone) {
  const tz = timeZone || clientTimeZone();
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(instant);
  } catch {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(instant);
  }
}

function partValue(parts, type) {
  const raw = parts.find((p) => p.type === type)?.value;
  if (type === "hour" && raw === "24") return "00";
  return raw;
}

/** YYYY-MM-DD for "today" in the given IANA timezone. */
export function todayYmdInTimeZone(timeZone) {
  const parts = partsInTimeZone(new Date(), timeZone);
  return `${partValue(parts, "year")}-${partValue(parts, "month")}-${partValue(parts, "day")}`;
}

/** Current wall-clock datetime in spot timezone — "YYYY-MM-DDTHH:MM", 30-min rounded up. */
export function nowInTimeZone(timeZone) {
  const parts = partsInTimeZone(new Date(), timeZone);
  const ymd = `${partValue(parts, "year")}-${partValue(parts, "month")}-${partValue(parts, "day")}`;
  let hour = Number(partValue(parts, "hour"));
  let minute = Number(partValue(parts, "minute"));
  if (minute % 30 !== 0) {
    minute += 30 - (minute % 30);
    if (minute >= 60) {
      minute = 0;
      hour += 1;
      if (hour >= 24) {
        const d = parseYMDLocal(ymd);
        if (d) {
          d.setDate(d.getDate() + 1);
          return `${formatYMD(d)}T00:00`;
        }
        return `${ymd}T00:00`;
      }
    }
  }
  return `${ymd}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/** Filter HH:MM slots to operating window (inclusive). Supports overnight close < open. */
export function filterOperatingTimeSlots(slots, operatingOpen, operatingClose) {
  if (!operatingOpen || !operatingClose) return slots;
  const toMin = (s) => {
    const [h, m] = s.split(":").map(Number);
    return h * 60 + m;
  };
  const openMin = toMin(operatingOpen);
  const closeMin = toMin(operatingClose);
  return slots.filter((s) => {
    const t = toMin(s);
    if (closeMin >= openMin) return t >= openMin && t <= closeMin;
    return t >= openMin || t <= closeMin;
  });
}

/** Parse API timestamps — naive ISO strings are stored as UTC in Mongo. */
export function parseApiTimestamp(isoStr) {
  if (!isoStr) return null;
  let s = String(isoStr).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
    s = `${s}Z`;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Local timestamp with timezone abbreviation (KAN-59). */
export function formatLocalTimestamp(isoStr, timeZone) {
  const d = parseApiTimestamp(isoStr);
  if (!d) return "";
  const tz = timeZone || clientTimeZone();
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: tz,
      timeZoneName: "short",
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  }
}

const PACIFIC_TIME_ZONES = new Set([
  "America/Vancouver", "America/Los_Angeles", "America/Tijuana",
  "America/Ensenada", "America/Santa_Isabel", "Canada/Pacific", "US/Pacific", "PST8PDT",
]);
const MOUNTAIN_TIME_ZONES = new Set([
  "America/Edmonton", "America/Denver", "America/Phoenix", "America/Whitehorse",
  "America/Dawson", "America/Dawson_Creek", "America/Fort_Nelson", "America/Creston",
  "Canada/Mountain", "US/Mountain", "US/Arizona",
]);
const CENTRAL_TIME_ZONES = new Set([
  "America/Winnipeg", "America/Chicago", "America/Mexico_City", "Canada/Central", "US/Central",
]);
const EASTERN_TIME_ZONES = new Set([
  "America/Toronto", "America/New_York", "America/Detroit", "America/Montreal",
  "Canada/Eastern", "US/Eastern",
]);

/** Renter-facing timezone label, e.g. "Pacific Time (PT)" — never IANA+MST (KAN-227). */
export function bookingTimeZoneLabel(booking) {
  const name = booking?.booking_timezone || booking?.vehicle_snapshot?.timezone;
  if (!name) return "";
  if (PACIFIC_TIME_ZONES.has(name)) return "Pacific Time (PT)";
  if (MOUNTAIN_TIME_ZONES.has(name)) return "Mountain Time (MT)";
  if (CENTRAL_TIME_ZONES.has(name)) return "Central Time (CT)";
  if (EASTERN_TIME_ZONES.has(name)) return "Eastern Time (ET)";
  const instant = parseApiTimestamp(booking?.start_at_utc)
    || parseDateTimeLocal(booking?.start_date)
    || new Date();
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: name,
      timeZoneName: "short",
    }).formatToParts(instant);
    const abbreviation = parts.find((part) => part.type === "timeZoneName")?.value;
    return abbreviation ? `${name} (${abbreviation})` : name;
  } catch {
    return "";
  }
}

export const MIN_BOOKING_MINUTES = 30;
export const MAX_TRIP_BILLABLE_DAYS = 28;

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatTripDateTime(d) {
  const weekday = WEEKDAYS_SHORT[d.getDay()];
  const month = MONTHS_SHORT[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const time = minutes === 0
    ? `${hours}:00 ${ampm}`
    : `${hours}:${String(minutes).padStart(2, "0")} ${ampm}`;
  return `${weekday}, ${month} ${day}, ${year} · ${time}`;
}

/** Human-readable trip window, e.g. "Jul 1, 2026 · 9:00 AM → Jul 2, 2026 · 9:00 AM · 1 day". */
export function formatTripRange(startStr, endStr) {
  const s = parseDateTimeLocal(startStr);
  const e = parseDateTimeLocal(endStr);
  if (!s || !e) return "";
  const days = billableDays(startStr, endStr);
  const dayLabel = days === 1 ? "1 day" : `${days} days`;
  return `${formatTripDateTime(s)} → ${formatTripDateTime(e)} · ${dayLabel}`;
}

/** Numeric date for trip summaries — weekday + dd/MM/yyyy (KAN-74). */
export function formatTripDateLabel(str) {
  const d = parseDateTimeLocal(str);
  if (!d) return "—";
  const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${weekday}, ${dd}/${mm}/${d.getFullYear()}`;
}

/** Clock time for trip summaries, e.g. "9:00 AM". */
export function formatTripTimeLabel(str) {
  const d = parseDateTimeLocal(str);
  if (!d) return "—";
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return minutes === 0
    ? `${hours}:00 ${ampm}`
    : `${hours}:${String(minutes).padStart(2, "0")} ${ampm}`;
}

/** Billable rental days label for trip summaries. */
export function formatTripDurationLabel(startStr, endStr) {
  const days = billableDays(startStr, endStr);
  if (!days) return "";
  return days === 1 ? "1 day" : `${days} days`;
}

/** Review stamp from API created_at — "Jun 2026". */
export function formatReviewMonthYear(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return "";
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/** Convert "HH:MM" 24h slot to 12h label, e.g. "09:00" → "9:00 AM". */
export function formatSlotLabel(slot) {
  if (!slot) return "";
  const [hStr, mStr] = slot.split(":");
  let h = Number(hStr);
  const m = mStr || "00";
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

/** Billable rental days: ceil(hours / 24), minimum 1. */
export function billableDays(startStr, endStr) {
  const s = parseDateTimeLocal(startStr);
  const e = parseDateTimeLocal(endStr);
  if (!s || !e || e <= s) return 0;
  const hours = (e - s) / 3600000;
  return Math.max(1, Math.ceil(hours / 24));
}

export function bookingWindowError(startStr, endStr, { allowPast = false } = {}) {
  const s = parseDateTimeLocal(startStr);
  const e = parseDateTimeLocal(endStr);
  if (!s || !e) return "Select valid pick-up and return times";
  if (!allowPast && s.getTime() < Date.now() - 60_000) {
    return "Pick-up cannot be in the past";
  }
  if (e <= s) return "Return must be after pick-up";
  const mins = (e - s) / 60000;
  if (mins < MIN_BOOKING_MINUTES) {
    return `Minimum rental is ${MIN_BOOKING_MINUTES} minutes`;
  }
  const days = billableDays(startStr, endStr);
  if (days > MAX_TRIP_BILLABLE_DAYS) {
    return `Booking must be 1-${MAX_TRIP_BILLABLE_DAYS} days`;
  }
  return null;
}

// Strip time + zero out so date-only comparisons are clean.
export function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

// Returns true if `day` (Date) overlaps any booked range.
// rangeStr/rangeEnd may be "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM".
export function dayOverlapsRanges(day, ranges) {
  if (!ranges || ranges.length === 0) return false;
  const t = startOfDay(day).getTime();
  return ranges.some((r) => {
    const s = parseYMDLocal(r.start);
    const e = parseYMDLocal(r.end);
    if (!s || !e) return false;
    return t >= startOfDay(s).getTime() && t <= startOfDay(e).getTime();
  });
}

// Returns true if a [start, end] window has any booked-range overlap (calendar days).
export function rangeOverlapsRanges(startStr, endStr, ranges) {
  if (!ranges || ranges.length === 0) return false;
  const s1 = parseYMDLocal(startStr); const e1 = parseYMDLocal(endStr);
  if (!s1 || !e1) return false;
  const sT = startOfDay(s1).getTime();
  const eT = startOfDay(e1).getTime();
  return ranges.some((r) => {
    const s = parseYMDLocal(r.start);
    const e = parseYMDLocal(r.end);
    if (!s || !e) return false;
    const rs = startOfDay(s).getTime();
    const re = startOfDay(e).getTime();
    return !(eT < rs || sT > re);
  });
}

/** Inclusive YYYY-MM-DD days for a trip window (matches backend `_booking_calendar_days`). */
export function eachCalendarDayYmd(startStr, endStr) {
  const s1 = parseYMDLocal(startStr);
  const e1 = parseYMDLocal(endStr);
  if (!s1 || !e1) return [];
  const start = startOfDay(s1);
  const end = startOfDay(e1);
  if (end.getTime() < start.getTime()) return [];
  const out = [];
  const cur = new Date(start);
  while (cur.getTime() <= end.getTime()) {
    out.push(formatYMD(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/**
 * Scheduled listing pause (KAN-193). Inclusive start, exclusive resume.
 * Missing resume_on means the pause runs until it is cancelled.
 */
export function dayInListingPause(day, listingPauses) {
  const ymd = typeof day === "string" ? day.slice(0, 10) : formatYMD(day);
  if (!ymd) return false;
  return (listingPauses || []).some((pause) => {
    const from = String(pause?.pause_from || "").slice(0, 10);
    const resume = String(pause?.resume_on || "").slice(0, 10);
    if (!from || ymd < from) return false;
    if (resume && ymd >= resume) return false;
    return true;
  });
}

export function rangeHitsScheduledPause(startStr, endStr, listingPauses) {
  const days = eachCalendarDayYmd(startStr, endStr);
  if (days.length === 0) return false;
  return days.some((d) => dayInListingPause(d, listingPauses));
}

/**
 * Host calendar unavailability (KAN-126 / KAN-29 / KAN-193).
 * - block_unavailable: `calendarDays` are blocked
 * - define_rentable: only `calendarDays` are bookable (empty ⇒ all unavailable)
 * - listingPauses override both modes
 */
export function dayIsHostUnavailable(day, calendarMode, calendarDays, listingPauses) {
  if (dayInListingPause(day, listingPauses)) return true;
  const ymd = typeof day === "string" ? day.slice(0, 10) : formatYMD(day);
  if (!ymd) return false;
  const set = new Set(calendarDays || []);
  if (calendarMode === "define_rentable") {
    return set.size === 0 || !set.has(ymd);
  }
  return set.has(ymd);
}

export function rangeHitsHostCalendar(startStr, endStr, calendarMode, calendarDays, listingPauses) {
  const days = eachCalendarDayYmd(startStr, endStr);
  if (days.length === 0) return false;
  return days.some((d) => dayIsHostUnavailable(d, calendarMode, calendarDays, listingPauses));
}

/** Full datetime overlap — for hourly parking where booked_ranges include clock times. */
export function rangeOverlapsDateTimes(startStr, endStr, ranges) {
  if (!ranges || ranges.length === 0) return false;
  const s1 = parseDateTimeLocal(startStr);
  const e1 = parseDateTimeLocal(endStr);
  if (!s1 || !e1) return false;
  const sT = s1.getTime();
  const eT = e1.getTime();
  return ranges.some((r) => {
    const s = parseDateTimeLocal(r.start);
    const e = parseDateTimeLocal(r.end);
    if (!s || !e) return false;
    return sT < e.getTime() && eT > s.getTime();
  });
}
